import PropTypes from "prop-types";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Box,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { IconArrowLeft, IconBrandWhatsapp, IconBriefcase, IconCheck, IconDownload, IconFile, IconMessages, IconPaperclip, IconRefresh, IconSearch, IconSend, IconX } from "@tabler/icons";
import PageContainer from "src/components/container/PageContainer";
import PageHeader from "src/components/shared/PageHeader";
import PersonAvatar from "src/components/shared/PersonAvatar";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import { useSocket } from "src/providers/SocketProvider";
import UrlApi from "src/utils/Url";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";
import { dateTime } from "src/pages/cases/format";
import { PAGE_SIZE, getAnswered, getChats, getMessages, setAnswered, uploadChatFile } from "./actions";
import { whatsappLink } from "src/pages/clients/whatsapp";

const fileUrl = (path) => (path ? `${UrlApi.baseUrlImageClient}${path}` : undefined);
const isFilePath = (text) => /^file\//.test(String(text || ""));
const SEND_TIMEOUT = 10000;

// "file/Passport scan-1765892984428.png" -> "Passport scan.png": the upload
// stamp is for the server, not for people.
function prettyFileName(path) {
  const base = String(path || "").split("/").pop();
  return base.replace(/-\d{10,}(?=\.[a-z0-9]{2,5}$)/i, "");
}

function preview(chat) {
  if (!chat.last_message) return "No messages yet";
  const image = chat.last_message_type === "image";
  if (image || chat.last_message_type === "file" || isFilePath(chat.last_message)) {
    return (
      <>
        <span aria-hidden="true">{image ? "📷 " : "📄 "}</span>
        {image ? "Photo" : "File"} · {prettyFileName(chat.last_message)}
      </>
    );
  }
  return chat.last_message;
}

const time = (date) => {
  const value = new Date(date);
  return Number.isNaN(value.getTime()) ? "" : value.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const dayLabel = (date) => {
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (value.toDateString() === today.toDateString()) return "Today";
  if (value.toDateString() === yesterday.toDateString()) return "Yesterday";
  return value.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// A photo that fails to load turns into a plain file link: never a broken image.
function MessageImage({ path }) {
  const [broken, setBroken] = useState(false);
  if (broken) return <MessageFile path={path} />;
  return (
    <Box component="a" href={fileUrl(path)} target="_blank" rel="noreferrer" sx={{ display: "block", lineHeight: 0 }}>
      <Box
        component="img"
        src={fileUrl(path)}
        alt="Photo sent in the chat"
        onError={() => setBroken(true)}
        sx={{ maxWidth: 240, width: "100%", borderRadius: 2, display: "block" }}
      />
    </Box>
  );
}

MessageImage.propTypes = { path: PropTypes.string };

function MessageFile({ path }) {
  return (
    <Stack component="a" href={fileUrl(path)} target="_blank" rel="noreferrer" direction="row" spacing={1} alignItems="center" sx={{ color: "inherit" }}>
      <IconFile size={18} />
      <Typography variant="body2" sx={{ textDecoration: "underline", wordBreak: "break-all" }}>
        {prettyFileName(path)}
      </Typography>
      <IconDownload size={16} />
    </Stack>
  );
}

MessageFile.propTypes = { path: PropTypes.string };

function Bubble({ msg, onRetry }) {
  const mine = Number(msg.sender_is_admin) === 1;
  const kind = msg.message_type === "image" ? "image" : msg.message_type === "file" ? "file" : "text";
  return (
    <Stack alignItems={mine ? "flex-end" : "flex-start"} sx={{ maxWidth: "100%" }}>
      <Box
        sx={{
          maxWidth: { xs: "85%", sm: "70%" },
          px: kind === "image" ? 0.5 : 1.5,
          py: kind === "image" ? 0.5 : 1,
          borderRadius: 3,
          ...(mine ? { borderBottomRightRadius: 6 } : { borderBottomLeftRadius: 6 }),
          bgcolor: mine ? "primary.main" : (t) => (t.palette.mode === "dark" ? "grey.800" : "#EAF0FA"),
          color: mine ? "primary.contrastText" : "text.primary",
          opacity: msg.pending ? 0.7 : 1,
        }}
      >
        {kind === "image" && <MessageImage path={msg.message} />}
        {kind === "file" && <MessageFile path={msg.message} />}
        {kind === "text" && (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.9375rem", lineHeight: 1.5 }}>
            {msg.message}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color={msg.failed ? "error" : "text.secondary"} sx={{ mt: 0.25, px: 0.5 }}>
        {mine && msg.name ? `${msg.name} · ` : ""}
        {msg.pending ? "Sending…" : msg.failed ? "Not sent" : time(msg.date)}
        {mine && !msg.pending && !msg.failed && msg.status_message === "isRead" ? " · Read" : ""}
        {msg.failed && (
          <Box
            component="button"
            type="button"
            onClick={() => onRetry(msg)}
            sx={{ ml: 1, p: 0, border: 0, bgcolor: "transparent", color: "primary.main", font: "inherit", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
          >
            Try again
          </Box>
        )}
      </Typography>
    </Stack>
  );
}

Bubble.propTypes = { msg: PropTypes.object.isRequired, onRetry: PropTypes.func.isRequired };

// Who is waiting: the API says who wrote last (0 = the customer) and how many
// of their messages no one has opened. Older APIs say neither; then the list
// keeps the server's order and the dot only covers this visit.
// How long a waiting customer has waited, from their last message: "5 h",
// "2 days" (round 2, rania-31). Empty without a date.
export function waitedFor(date, now = Date.now()) {
  const at = Date.parse(date || "");
  if (!Number.isFinite(at)) return "";
  const hours = Math.max(Math.floor((now - at) / 3600000), 0);
  if (hours < 1) return "under 1 h";
  if (hours < 48) return `${hours} h`;
  return `${Math.floor(hours / 24)} days`;
}

// A chat someone marked answered (on WhatsApp or by phone) waits no more,
// until the customer writes again (round 2, karim-15).
const waiting = (chat) => chat.last_sender_is_admin === 0 && !chat.answered;
const unread = (chat) => Number(chat.unread_count) || 0;
const byWaiting = (a, b) =>
  Number(waiting(b)) - Number(waiting(a)) || unread(b) - unread(a) || new Date(b.date || 0) - new Date(a.date || 0);

function ChatList({ chats, state, selectedId, live, onSelect, onRetry, version }) {
  const [filter, setFilter] = useState("");
  const knowsWaiting = chats.some((c) => "last_sender_is_admin" in c);
  const waitingCount = chats.filter(waiting).length;
  // The order is set when the list loads and kept until it reloads, so
  // opening a chat (which marks it read) never moves the rows under the
  // pointer. A chat not seen at load (a new one) goes on top.
  const rank = useRef({ version: null, ids: new Map() });
  if (rank.current.version !== version && chats.length) {
    const sorted = knowsWaiting ? chats.slice().sort(byWaiting) : chats;
    rank.current = { version, ids: new Map(sorted.map((c, i) => [c.id, i])) };
  }
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = q
      ? chats.filter((c) => String(c.name || "").toLowerCase().includes(q) || String(c.email || "").toLowerCase().includes(q))
      : chats;
    const at = (c) => rank.current.ids.get(c.id) ?? -1;
    return rows.slice().sort((a, b) => at(a) - at(b));
  }, [chats, filter]);
  return (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      <Box sx={{ p: 2, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="h6" component="h2">
          Conversations{chats.length ? ` (${chats.length})` : ""}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="p" mb={1}>
          {knowsWaiting
            ? waitingCount
              ? `${waitingCount === 1 ? "1 customer is" : `${waitingCount} customers are`} waiting for a reply. They come first.`
              : "Every customer has had a reply."
            : live
              ? "A dot marks new messages since you opened this page. Older unread messages are not marked yet."
              : "Unread messages are not marked: that needs the live chat connection."}
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Find a customer: name or email"
          inputProps={{ "aria-label": "Find a customer by name or email" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconSearch size={18} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {state.loading && !chats.length && (
          <Box p={2} aria-busy="true" aria-label="Loading conversations">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={52} sx={{ mb: 1 }} />
            ))}
          </Box>
        )}
        {state.failed && !chats.length && (
          <Alert
            severity="error"
            sx={{ m: 2 }}
            action={
              <ActionButton onClick={onRetry} startIcon={<IconRefresh size={16} />}>
                Try again
              </ActionButton>
            }
          >
            The conversations did not load.
          </Alert>
        )}
        {!state.loading && !state.failed && chats.length === 0 && (
          <Typography variant="body2" color="text.secondary" p={2}>
            No conversations yet. They appear here when a customer writes from their account on safrati.ai.
          </Typography>
        )}
        {chats.length > 0 && shown.length === 0 && (
          <Typography variant="body2" color="text.secondary" p={2}>
            No customer matches “{filter}”.
          </Typography>
        )}
        <List disablePadding>
          {shown.map((chat) => (
            <ListItem key={chat.id} disablePadding>
              <ListItemButton
                selected={selectedId === chat.id}
                onClick={() => onSelect(chat.id)}
                sx={{ alignItems: "flex-start", py: 1.25, borderBottom: 1, borderColor: "divider", borderRadius: 0 }}
              >
                <ListItemAvatar sx={{ minWidth: 52 }}>
                  <Badge
                    color="secondary"
                    overlap="circular"
                    variant={unread(chat) ? "standard" : "dot"}
                    badgeContent={unread(chat) || undefined}
                    invisible={!(chat.hasNewMessage || unread(chat))}
                  >
                    <PersonAvatar name={chat.name || "Customer"} src={chat.image} size={40} />
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  disableTypography
                  primary={
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography fontWeight={chat.hasNewMessage || waiting(chat) ? 700 : 600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {chat.name?.trim() || "Customer"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                        {dayLabel(chat.date) === "Today" ? time(chat.date) : dayLabel(chat.date)}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <>
                      {chat.email && (
                        <Typography variant="caption" color="text.secondary" noWrap component="p">
                          {chat.email}
                        </Typography>
                      )}
                      <Typography variant="body2" color={chat.hasNewMessage ? "text.primary" : "text.secondary"} noWrap>
                        {/* Hidden for the eye, read out (sx width: 1 was 100%:
                            the list scrolled sideways at 200 % zoom, QA georges-25). */}
                        {(chat.hasNewMessage || waiting(chat)) && (
                          <Box component="span" sx={visuallyHidden}>
                            {unread(chat) ? `${unread(chat)} unread, waiting for a reply: ` : waiting(chat) ? "Waiting for a reply: " : "New message: "}
                          </Box>
                        )}
                        {waiting(chat) && (
                          <Box component="span" sx={{ color: (t) => (t.palette.mode === "dark" ? t.palette.secondary.main : t.palette.secondary.dark), fontWeight: 600 }} aria-hidden>
                            Waiting{waitedFor(chat.date) ? ` ${waitedFor(chat.date)}` : ""} ·{" "}
                          </Box>
                        )}
                        {preview(chat)}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Stack>
  );
}

ChatList.propTypes = {
  chats: PropTypes.array.isRequired,
  state: PropTypes.object.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  live: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  version: PropTypes.number,
};

// Ctrl+Enter (⌘+Enter on a Mac) sends; Enter alone adds a line, so a long,
// careful reply never goes half-written (QA nadia-28).
export const sendsMessage = (event) => event.key === "Enter" && !!(event.ctrlKey || event.metaKey);

export default function Support() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // ?customer=<client id>: the case's "Messages" link opens their chat.
  const [params] = useSearchParams();
  const wanted = Number(params.get("customer")) || null;
  const [notFound, setNotFound] = useState(false);
  const phone = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const { socket, configured, connected } = useSocket();
  const privileges = getLocalStorage(dataLocalStorage.privileges) || {};
  const canReply = Number(privileges["25"]?.access_write) === 1 && Number(privileges["25"]?.access_edit) === 1;
  const me = getLocalStorage(dataLocalStorage.userinfo) || {};

  const [chats, setChats] = useState([]);
  const [chatState, setChatState] = useState({ loading: true, failed: false });
  // Marked answered, and each chat's customer contact: { ready, answered: Map, contacts: Map }.
  const [extras, setExtras] = useState({ ready: null, answered: new Map(), contacts: new Map() });
  const canMark = Number(privileges["25"]?.access_edit) === 1;
  // Bumped on every load: the list re-sorts then, and only then.
  const [listVersion, setListVersion] = useState(0);
  const touch = useMediaQuery("(hover: none) and (pointer: coarse)");
  const [selectedId, setSelectedId] = useState(null);
  // messages are kept newest first, the way the API sends them.
  const [thread, setThread] = useState({ loading: false, failed: false, more: false, olderLoading: false, messages: [] });
  const [input, setInput] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInput = useRef(null);
  const selectedRef = useRef(null);
  selectedRef.current = selectedId;

  const scrollToEnd = () => {
    requestAnimationFrame(() => {
      const box = scrollRef.current;
      if (box) box.scrollTop = box.scrollHeight;
    });
  };

  const loadChats = useCallback(() => {
    setChatState({ loading: true, failed: false });
    return getChats(dispatch)
      .then((response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        setChats(rows);
        setListVersion((v) => v + 1);
        setChatState({ loading: false, failed: false });
        if (rows.length)
          getAnswered(
            rows.map((c) => c.id),
            dispatch,
          )
            .then((res) => {
              const d = res?.data || {};
              setExtras({
                ready: d.ready ?? null,
                answered: new Map((d.answered || []).map((a) => [Number(a.chat_id), a])),
                contacts: new Map((d.contacts || []).map((c) => [Number(c.chat_id), c])),
              });
              setListVersion((v) => v + 1);
            })
            .catch(() => {
              /* an older server: no marks, no WhatsApp */
            });
      })
      .catch(() => setChatState({ loading: false, failed: true }));
  }, [dispatch]);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  // The customer the address names, once the list is in: their chat, or a
  // line saying they haven't written.
  const askedFor = useRef(null);
  useEffect(() => {
    if (!wanted || chatState.loading || askedFor.current === wanted) return;
    askedFor.current = wanted;
    const chat = chats.find((c) => Number(c.client_id) === wanted);
    if (chat) openChat(chat.id);
    else setNotFound(true);
    // openChat is stable; only a new list or address matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, chatState.loading, chats]);

  // "Mark as read": the customer's messages are read for the whole team.
  const markRead = (chatId) =>
    getMessages(chatId, 0, dispatch, { markRead: true })
      .then(() => {
        setChats((prev) => prev.map((c) => (c.id === chatId ? { ...c, unread_count: 0, hasNewMessage: false } : c)));
        toast.success("Marked as read for the team.");
      })
      .catch(() => toast.error("Couldn’t mark it as read. Nothing changed. Try again."));

  const openChat = useCallback(
    (chatId) => {
      setSelectedId(chatId);
      setInput("");
      setFile(null);
      // Opening a chat doesn't mark it read: it stays unread until someone
      // replies or presses "Mark as read", so a glance never hides it from
      // the team (QA nadia-28). The live dot of this visit goes.
      setChats((prev) => prev.map((c) => (c.id === chatId ? { ...c, hasNewMessage: false } : c)));
      setThread({ loading: true, failed: false, more: false, olderLoading: false, messages: [] });
      getMessages(chatId, 0, dispatch, { markRead: false })
        .then((response) => {
          if (selectedRef.current !== chatId) return;
          const rows = Array.isArray(response?.data) ? response.data : [];
          setThread({ loading: false, failed: false, more: rows.length === PAGE_SIZE, olderLoading: false, messages: rows });
          scrollToEnd();
        })
        .catch(() => selectedRef.current === chatId && setThread((t) => ({ ...t, loading: false, failed: true })));
    },
    [dispatch],
  );

  const loadOlder = () => {
    const chatId = selectedId;
    const box = scrollRef.current;
    const fromBottom = box ? box.scrollHeight - box.scrollTop : 0;
    setThread((t) => ({ ...t, olderLoading: true }));
    getMessages(chatId, thread.messages.filter((m) => m.id).length, dispatch, { markRead: false })
      .then((response) => {
        if (selectedRef.current !== chatId) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        setThread((t) => {
          const known = new Set(t.messages.map((m) => m.id));
          return { ...t, olderLoading: false, more: rows.length === PAGE_SIZE, messages: [...t.messages, ...rows.filter((m) => !known.has(m.id))] };
        });
        // Keep the reader where they were.
        requestAnimationFrame(() => {
          if (box) box.scrollTop = box.scrollHeight - fromBottom;
        });
      })
      .catch(() => setThread((t) => ({ ...t, olderLoading: false })));
  };

  // Live updates, when the socket is connected.
  useEffect(() => {
    if (!socket) return undefined;
    const onMessage = (message) => {
      if (String(message?.id_chat) !== String(selectedRef.current)) return;
      setThread((t) => (t.messages.some((m) => m.id && m.id === message.id) ? t : { ...t, messages: [message, ...t.messages] }));
      scrollToEnd();
    };
    const onChat = (chat) => {
      if (!chat?.id) return;
      const isUnread = chat.id !== selectedRef.current;
      setChats((prev) => {
        const rest = prev.filter((c) => c.id !== chat.id);
        const old = prev.find((c) => c.id === chat.id) || {};
        const fromCustomer = chat.sender_is_admin === 0 || chat.last_sender_is_admin === 0;
        return [
          {
            ...old,
            ...chat,
            date: chat.date || new Date().toISOString(),
            hasNewMessage: isUnread,
            ...(fromCustomer && "last_sender_is_admin" in old
              ? { last_sender_is_admin: 0, unread_count: isUnread ? (Number(old.unread_count) || 0) + 1 : 0 }
              : {}),
          },
          ...rest,
        ];
      });
      if (isUnread) toast.info(`New message from ${chat.name || "a customer"}`, { autoClose: 3000 });
    };
    socket.on("receive_message", onMessage);
    socket.on("update_chat", onChat);
    socket.on("receivechat", onChat);
    return () => {
      socket.off("receive_message", onMessage);
      socket.off("update_chat", onChat);
      socket.off("receivechat", onChat);
    };
  }, [socket]);

  // Sends over the socket and waits for the server's receipt.
  const emit = (payload) =>
    new Promise((resolve, reject) => {
      if (!socket || !connected) {
        reject(new Error("offline"));
        return;
      }
      const timer = setTimeout(() => reject(new Error("timeout")), SEND_TIMEOUT);
      socket.emit("send_message", payload, (id) => {
        clearTimeout(timer);
        resolve(id);
      });
    });

  const deliver = async (draft) => {
    const key = draft.key;
    setThread((t) => ({ ...t, messages: [{ ...draft, pending: true, failed: false }, ...t.messages.filter((m) => m.key !== key)] }));
    scrollToEnd();
    try {
      let message = draft.message;
      if (draft.file) {
        const uploaded = await uploadChatFile(draft.file, dispatch);
        if (!uploaded?.data) throw new Error("upload");
        message = uploaded.data;
      }
      const id = await emit({ message, message_type: draft.message_type, id_chat: draft.id_chat });
      setThread((t) => ({
        ...t,
        messages: t.messages.map((m) => (m.key === key ? { ...m, id: typeof id === "number" ? id : m.id, message, file: undefined, pending: false } : m)),
      }));
      setChats((prev) =>
        prev.map((c) =>
          c.id === draft.id_chat
            ? {
                ...c,
                last_message: message,
                last_message_type: draft.message_type,
                date: new Date().toISOString(),
                // Answered: no longer waiting for a reply, and read.
                unread_count: 0,
                ...("last_sender_is_admin" in c ? { last_sender_is_admin: 1 } : {}),
              }
            : c,
        ),
      );
      return true;
    } catch {
      setThread((t) => ({ ...t, messages: t.messages.map((m) => (m.key === key ? { ...m, pending: false, failed: true } : m)) }));
      return false;
    }
  };

  const send = async () => {
    if (sending || !selectedId) return;
    const text = input.trim();
    if (!text && !file) return;
    const base = {
      key: `local-${Date.now()}`,
      id_chat: selectedId,
      sender_id: me.id,
      sender_is_admin: 1,
      name: me.fullname || "",
      date: new Date().toISOString(),
    };
    const draft = file
      ? { ...base, message: file.name, file, message_type: /^image\//.test(file.type) ? "image" : "file" }
      : { ...base, message: text, message_type: "text" };
    setSending(true);
    setInput("");
    setFile(null);
    const ok = await deliver(draft);
    setSending(false);
    if (!ok && !file) setInput(text);
    inputRef.current?.focus();
  };

  const shownChats = useMemo(() => chats.map((c) => (extras.answered.has(Number(c.id)) ? { ...c, answered: extras.answered.get(Number(c.id)) } : c)), [chats, extras]);
  const selected = shownChats.find((c) => c.id === selectedId);
  const contact = selected ? extras.contacts.get(Number(selected.id)) : null;
  const whatsapp = contact?.phone ? whatsappLink(contact.phone, contact.case_id) : null;
  // Mark as answered, with Undo in its message.
  const markAnswered = async (chatId, undo = false) => {
    try {
      await setAnswered(chatId, undo, dispatch);
      setExtras((x) => {
        const answered = new Map(x.answered);
        if (undo) answered.delete(Number(chatId));
        else answered.set(Number(chatId), { chat_id: Number(chatId), by: me?.fullname || null, answered_at: new Date().toISOString() });
        return { ...x, answered };
      });
      const who = chats.find((c) => c.id === chatId)?.name?.trim() || "The customer";
      if (undo) toast.info(`${who} is waiting for a reply again.`);
      else
        toast.success(
          <Box>
            <Typography variant="body2">{who}’s chat is marked answered: it no longer waits. It waits again when they write.</Typography>
            <ActionButton variant="text" onClick={() => markAnswered(chatId, true)} sx={{ mt: 0.5, ml: -1.5 }}>
              Undo
            </ActionButton>
          </Box>,
          { autoClose: 8000 },
        );
    } catch (err) {
      toast.error(err?.status === 503 ? "Marking chats answered needs database update 043. Nothing changed." : "The chat wasn’t marked answered. Nothing changed. Try again.");
    }
  };
  const live = configured && connected;
  const showList = !phone || !selectedId;
  const showThread = !phone || !!selectedId;
  // Oldest at the top, with a line for each new day.
  const ordered = thread.messages.slice().reverse();

  return (
    <PageContainer title="Support chats · Safrati CMS" description="Customer support chat">
      <PageHeader title="Support chats" subtitle="Customers’ messages from the chat on safrati.ai." sx={{ mb: 2 }} />

      {notFound && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotFound(false)}>
          This customer hasn’t written in the support chat yet.
        </Alert>
      )}

      {!live && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {configured
            ? "Not connected to the live chat. New messages will not appear until the connection is back, and replies cannot be sent."
            : "Replies cannot be sent from the CMS yet: the live chat server is not set up. Answer on WhatsApp or by phone, then Mark as answered. New messages show when you reload."}
        </Alert>
      )}

      <Card
        variant="outlined"
        sx={{ display: "flex", height: { xs: "calc(100vh - 220px)", md: "calc(100vh - 240px)" }, minHeight: 440, p: 0, overflow: "hidden" }}
      >
        {showList && (
          <Box sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0, borderRight: { md: 1 }, borderColor: { md: "divider" }, minHeight: 0 }}>
            <ChatList chats={shownChats} state={chatState} selectedId={selectedId} live={live} onSelect={openChat} onRetry={loadChats} version={listVersion} />
          </Box>
        )}

        {showThread && (
          <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            {!selectedId ? (
              <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ flex: 1, p: 4, textAlign: "center", color: "text.secondary" }}>
                <IconMessages size={32} />
                <Typography fontWeight={600} color="text.primary">
                  Pick a conversation
                </Typography>
                <Typography variant="body2">Choose a customer on the left to read their messages.</Typography>
              </Stack>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
                  {phone && (
                    <IconButton aria-label="Back to conversations" onClick={() => setSelectedId(null)} sx={iconButtonSx}>
                      <IconArrowLeft size={20} />
                    </IconButton>
                  )}
                  <PersonAvatar name={selected?.name || "Customer"} src={selected?.image} size={36} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>
                      {selected?.name?.trim() || "Customer"}
                    </Typography>
                    {(selected?.email || selected?.date) && (
                      <Typography variant="caption" color="text.secondary" noWrap component="p">
                        {selected.email}
                        {selected.email && selected.date ? " · " : ""}
                        {selected.date && `Last message ${dateTime(selected.date)}`}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ ml: "auto !important", flexShrink: 0 }}>
                    {/* Unread stays until someone replies or marks it read. */}
                    {unread(selected || {}) > 0 && canReply && (
                      <ActionButton variant="text" busyLabel="Saving…" onClick={() => markRead(selectedId)}>
                        Mark as read
                      </ActionButton>
                    )}
                    {/* Answer outside the CMS: WhatsApp opens with the number and
                        the case number typed in; Safrati sends nothing (karim-15). */}
                    {whatsapp && (
                      <ActionButton
                        variant="text"
                        component="a"
                        href={whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<IconBrandWhatsapp size={16} />}
                        aria-label={`Answer ${selected?.name?.trim() || "the customer"} on WhatsApp${contact?.case_id ? `, about case #${contact.case_id}` : ""} (opens WhatsApp; nothing is sent until you send it)`}
                      >
                        {phone ? "WhatsApp" : "Answer on WhatsApp"}
                      </ActionButton>
                    )}
                    {waiting(selected || {}) && canMark && extras.ready !== false && (
                      <ActionButton variant="text" startIcon={<IconCheck size={16} />} busyLabel="Saving…" onClick={() => markAnswered(selectedId)}>
                        {phone ? "Answered" : "Mark as answered"}
                      </ActionButton>
                    )}
                    {/* The customer's applications, to answer with the case at hand. */}
                    {(selected?.email || selected?.name?.trim()) && (
                      <ActionButton
                        variant="text"
                        startIcon={<IconBriefcase size={16} />}
                        // By email when the API gives it (exact), across every view.
                        onClick={() => navigate(`/cases?q=${encodeURIComponent(selected.email || selected.name.trim())}&view=all`)}
                      >
                        {phone ? "Applications" : "Their applications"}
                      </ActionButton>
                    )}
                  </Stack>
                </Stack>

                <Box
                  ref={scrollRef}
                  role="log"
                  aria-live="polite"
                  aria-label={`Messages with ${selected?.name?.trim() || "the customer"}`}
                  tabIndex={0}
                  aria-busy={thread.loading || undefined}
                  sx={{ flex: 1, overflowY: "auto", px: { xs: 1.5, sm: 2.5 }, py: 2, bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "#F7FAFC") }}
                >
                  {thread.loading && (
                    <Stack alignItems="center" spacing={1} py={4} role="status">
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary">
                        Loading messages…
                      </Typography>
                    </Stack>
                  )}
                  {thread.failed && (
                    <Alert
                      severity="error"
                      action={
                        <ActionButton onClick={() => openChat(selectedId)} startIcon={<IconRefresh size={16} />}>
                          Try again
                        </ActionButton>
                      }
                    >
                      The messages did not load.
                    </Alert>
                  )}
                  {!thread.loading && !thread.failed && ordered.length === 0 && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                      No messages in this conversation yet.
                    </Typography>
                  )}
                  {thread.more && !thread.loading && (
                    <Box textAlign="center" mb={2}>
                      <ActionButton variant="text" busy={thread.olderLoading} busyLabel="Loading…" onClick={loadOlder}>
                        Show older messages
                      </ActionButton>
                    </Box>
                  )}
                  <Stack spacing={1.25}>
                    {ordered.map((msg, index) => {
                      const day = dayLabel(msg.date);
                      const newDay = index === 0 || dayLabel(ordered[index - 1].date) !== day;
                      return (
                        <Fragment key={msg.id ?? msg.key ?? index}>
                          {newDay && day && (
                            <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ display: "block", fontWeight: 600, pt: index ? 1 : 0 }}>
                              {day}
                            </Typography>
                          )}
                          <Bubble msg={msg} onRetry={(m) => !sending && deliver(m)} />
                        </Fragment>
                      );
                    })}
                  </Stack>
                </Box>

                {canReply && (
                  <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
                    {file && (
                      <Stack direction="row" alignItems="center" spacing={1} mb={1} sx={{ px: 1, py: 0.5, borderRadius: 2, bgcolor: "action.hover" }}>
                        <IconFile size={18} />
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                          {file.name}
                        </Typography>
                        <IconButton size="small" aria-label="Remove the file" onClick={() => setFile(null)} sx={iconButtonSx}>
                          <IconX size={16} />
                        </IconButton>
                      </Stack>
                    )}
                    <Stack direction="row" spacing={1} alignItems="flex-end">
                      <Tooltip title={live ? "Attach a photo or PDF" : "Replies cannot be sent right now"}>
                        <span>
                          <IconButton aria-label="Attach a photo or PDF" disabled={!live || sending} onClick={() => fileInput.current?.click()} sx={iconButtonSx}>
                            <IconPaperclip size={20} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <input
                        ref={fileInput}
                        hidden
                        type="file"
                        tabIndex={-1}
                        aria-hidden="true"
                        accept="application/pdf,image/*"
                        onChange={(ev) => {
                          setFile(ev.target.files?.[0] || null);
                          ev.target.value = "";
                        }}
                      />
                      <TextField
                        inputRef={inputRef}
                        fullWidth
                        multiline
                        maxRows={5}
                        size="small"
                        value={input}
                        disabled={!live || !!file}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (sendsMessage(e)) {
                            e.preventDefault();
                            send();
                          }
                        }}
                        placeholder={!live ? "Replies cannot be sent right now" : file ? "Send the file, or remove it to type" : "Write a reply"}
                        inputProps={{ "aria-label": "Reply", maxLength: 4000 }}
                      />
                      <ActionButton
                        variant="contained"
                        busy={sending}
                        busyLabel="Sending…"
                        disabled={!live || (!input.trim() && !file)}
                        startIcon={<IconSend size={16} />}
                        onClick={send}
                      >
                        Send
                      </ActionButton>
                    </Stack>
                    {live && !touch && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, px: 0.5 }}>
                        Ctrl+Enter sends (⌘+Enter on a Mac). Enter adds a line.
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Stack>
        )}
      </Card>
    </PageContainer>
  );
}
